import { getApiBaseUrl } from "../config/runtimeConfig";
import { invalidateLookup } from "../lookups/lookupsApi";

const apiBaseUrl = getApiBaseUrl();

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export type BudgetStatus =
  | "elaboracao"
  | "aguardando"
  | "pronto"
  | "enviado"
  | "aceite"
  | "recusado";

export const budgetStatusLabels: Record<BudgetStatus, string> = {
  elaboracao: "Em Elaboração",
  aguardando: "Aguardando Preços",
  pronto: "Pronto para Enviar",
  enviado: "Enviado",
  aceite: "Adjudicado",
  recusado: "Não Adjudicado",
};

const normalizedStatusMap: Record<string, BudgetStatus> = {
  elaboracao: "elaboracao",
  "em elaboração": "elaboracao",
  "em elaboracao": "elaboracao",
  aguardando: "aguardando",
  "aguardando preços": "aguardando",
  "aguardando precos": "aguardando",
  pronto: "pronto",
  "pronto para enviar": "pronto",
  enviado: "enviado",
  aceite: "aceite",
  adjudicado: "aceite",
  recusado: "recusado",
  "não adjudicado": "recusado",
  "nao adjudicado": "recusado",
};

export const normalizeBudgetStatus = (status: string): BudgetStatus => {
  const key = status.trim().toLowerCase();
  return normalizedStatusMap[key] ?? "elaboracao";
};

export const formatBudgetStatusLabel = (status: BudgetStatus): string =>
  budgetStatusLabels[status] ?? status;

export interface BudgetSummary {
  id: number;
  reference: string;
  versionNumber: number;
  versionLabel?: string | null;
  isActiveVersion: boolean;
  status: string;
  clientId: number | null;
  clientName: string;
  workName: string;
  totalAmount?: number | null;
  created: string;
  lastModified: string;
}

export interface Budget {
  id: number;
  reference: string;
  versionGroupId: string;
  versionNumber: number;
  versionLabel?: string | null;
  isActiveVersion: boolean;
  status: string;
  clientId: number | null;
  clientName: string;
  workId?: number | null;
  workCode?: string | null;
  workName: string;
  responsible?: string | null;
  orderReference?: string | null;
  vatRate: number;
  totalAmount?: number | null;
  workArea?: number | null;
  rejectionReason?: string | null;
  lines: BudgetLine[];
  documents: BudgetDocument[];
  created: string;
  lastModified: string;
}

export interface BudgetLine {
  id: number;
  areaName: string;
  chapterName: string;
  chapterNote?: string | null;
  code: string;
  description: string;
  unit: string;
  quantity: number;
  quantityFormula?: string | null;
  quantityFormulaNote?: string | null;
  unitPrice: number;
  isPercentageOfWorkTotal: boolean;
  percentageOfWorkTotal?: number | null;
  marginPercent?: number | null;
  costUnitPrice?: number | null;
  isCostPriceValidated?: boolean | null;
  costSuggestionBudgetId?: number | null;
  costSuggestionBudgetReference?: string | null;
  note?: string | null;
  internalTeamActive: boolean;
  internalTeamValue?: number | null;
  internalTeamNotes?: string | null;
  subcontractorId?: number | null;
  subcontractors: BudgetLineSubcontractor[];
  suppliers: BudgetLineSupplier[];
  materials: BudgetLineMaterial[];
}

export interface BudgetLineSubcontractor {
  id: number;
  subcontractorId: number;
  subcontractorName?: string | null;
  description: string;
  value: number;
}

export interface BudgetLineSupplier {
  id: number;
  supplierId: number;
  supplierName?: string | null;
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  value: number;
}

export interface BudgetLineMaterial {
  id: number;
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  value: number;
}

export interface BudgetDocument {
  id: number;
  name: string;
  sizeInBytes: number;
  url?: string | null;
  processingStatus?: string | null;
  processingError?: string | null;
}

export interface BudgetPayload {
  reference: string;
  versionLabel?: string | null;
  clientId?: number | null;
  workId?: number | null;
  workName: string;
  responsible?: string | null;
  orderReference?: string | null;
  vatRate: number;
  totalAmount?: number | null;
  workArea?: number | null;
  rejectionReason?: string | null;
  lines?: BudgetLinePayload[];
  documents?: BudgetDocumentPayload[];
}

export interface BudgetLinePayload {
  areaName: string;
  chapterName: string;
  chapterNote?: string | null;
  code: string;
  description: string;
  unit: string;
  quantity: number;
  quantityFormula?: string | null;
  quantityFormulaNote?: string | null;
  unitPrice: number;
  isPercentageOfWorkTotal?: boolean | null;
  percentageOfWorkTotal?: number | null;
  marginPercent?: number | null;
  costUnitPrice?: number | null;
  isCostPriceValidated?: boolean | null;
  costSuggestionBudgetId?: number | null;
  costSuggestionBudgetReference?: string | null;
  note?: string | null;
  internalTeamActive: boolean;
  internalTeamValue?: number | null;
  internalTeamNotes?: string | null;
  subcontractorId?: number | null;
  subcontractors: BudgetLineSubcontractorPayload[];
  suppliers: BudgetLineSupplierPayload[];
  materials: BudgetLineMaterialPayload[];
}

export interface BudgetLineSubcontractorPayload {
  subcontractorId: number;
  description: string;
  value: number;
}

export interface BudgetLineSupplierPayload {
  supplierId: number;
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  value: number;
}

export interface BudgetLineMaterialPayload {
  description: string;
  quantity?: number | null;
  unitPrice?: number | null;
  value: number;
}

export interface BudgetLineCostSuggestionRequest {
  query: string;
  unit?: string;
  limit?: number;
}

export interface BudgetLineCostSuggestion {
  budgetId: number;
  budgetReference: string;
  budgetDate: string;
  clientName: string;
  workName: string;
  lineCode: string;
  lineDescription: string;
  unit: string;
  costUnitPrice: number;
}

export interface BudgetDocumentPayload {
  name: string;
  sizeInBytes: number;
  url?: string | null;
}

export interface BudgetVersionSummary {
  id: number;
  versionNumber: number;
  versionLabel?: string | null;
  isActiveVersion: boolean;
  status: string;
  created: string;
}

export interface WorkFromBudget {
  id: number;
  code: string;
  name: string;
  clientId: number;
  clientName: string;
  created: string;
  lastModified: string;
}

export interface CreateBudgetVersionPayload {
  sourceBudgetId?: number;
  versionLabel?: string | null;
}

export interface UpdateBudgetStatusPayload {
  status: BudgetStatus;
  rejectionReason?: string | null;
}

export interface BudgetAreaType {
  id: number;
  name: string;
  sortOrder: number;
}

export interface BudgetChapterType {
  id: number;
  budgetAreaTypeId: number;
  code: string;
  name: string;
  sortOrder: number;
}

export interface BudgetAreaTypePayload {
  name: string;
  sortOrder?: number;
}

export interface BudgetChapterTypePayload {
  budgetAreaTypeId: number;
  code?: string | null;
  name: string;
  sortOrder?: number;
}

export interface BudgetQuantityImportArticle {
  number: string;
  description: string;
  unit: string;
  quantity: number;
  measurementSource?: string | null;
  measurementNote?: string | null;
  suggestedCostUnitPrice?: number | null;
  suggestedCostMargin?: number | null;
  suggestedCostBudgetId?: number | null;
  suggestedCostBudgetReference?: string | null;
}

export interface BudgetQuantityImportChapter {
  name: string;
  note?: string | null;
  articles: BudgetQuantityImportArticle[];
}

export interface BudgetQuantityImportArea {
  name: string;
  chapters: BudgetQuantityImportChapter[];
}

export interface BudgetQuantityImportPreview {
  areas: BudgetQuantityImportArea[];
  totalCapitulos: number;
  totalArtigos: number;
}

export type BudgetQuantityImportJobStatus =
  | "Pending"
  | "Running"
  | "Succeeded"
  | "Failed";

export type BudgetQuantityImportJobStage =
  | "Uploading"
  | "OcrExtracting"
  | "Condensing"
  | "AiExtracting"
  | "Parsing"
  | "Merging"
  | "Saving"
  | "Done"
  | "Failed";

export interface BudgetQuantityImportJobCreated {
  jobId: string;
}

export interface BudgetQuantityImportJobResult {
  budgetId: number;
  documentId?: number | null;
  areas: number;
  chapters: number;
  articles: number;
  preview?: BudgetQuantityImportPreview | null;
}

export interface BudgetQuantityImportJobStatusResponse {
  jobId: string;
  budgetId: number;
  status: BudgetQuantityImportJobStatus;
  stage: BudgetQuantityImportJobStage;
  message: string;
  percent?: number | null;
  elapsedMs?: number | null;
  startedAt?: string | null;
  updatedAt?: string | null;
  completedAt?: string | null;
  result?: BudgetQuantityImportJobResult | null;
  error?: string | null;
}

async function request<T>(
  path: string,
  options?: RequestInit,
): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(
      response.status,
      text || response.statusText,
    );
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

export function getBudgets(): Promise<BudgetSummary[]> {
  return request<BudgetSummary[]>("/api/budgets");
}

export function getBudgetById(budgetId: number): Promise<Budget> {
  return request<Budget>(`/api/budgets/${budgetId}`);
}

export function getBudgetVersions(
  budgetId: number,
): Promise<BudgetVersionSummary[]> {
  return request<BudgetVersionSummary[]>(
    `/api/budgets/${budgetId}/versions`,
  );
}

export function createBudget(payload: BudgetPayload): Promise<Budget> {
  return request<Budget>("/api/budgets", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateBudget(
  budgetId: number,
  payload: BudgetPayload,
): Promise<Budget> {
  return request<Budget>(`/api/budgets/${budgetId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateBudgetStatus(
  budgetId: number,
  payload: UpdateBudgetStatusPayload,
): Promise<Budget> {
  return request<Budget>(`/api/budgets/${budgetId}/status`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function createWorkFromBudget(
  budgetId: number,
): Promise<WorkFromBudget> {
  return request<WorkFromBudget>(`/api/budgets/${budgetId}/work`, {
    method: "POST",
  }).then((result) => {
    invalidateLookup("works");
    return result;
  });
}

export function createBudgetVersion(
  budgetId: number,
  payload: CreateBudgetVersionPayload,
): Promise<Budget> {
  return request<Budget>(`/api/budgets/${budgetId}/versions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function setActiveBudgetVersion(
  budgetId: number,
): Promise<Budget> {
  return request<Budget>(`/api/budgets/${budgetId}/versions/activate`, {
    method: "PUT",
  });
}

export function uploadBudgetDocument(
  budgetId: number,
  file: File,
): Promise<BudgetDocument> {
  const formData = new FormData();
  formData.append("document", file, file.name || "documento");

  return request<BudgetDocument>(`/api/budgets/${budgetId}/documents`, {
    method: "POST",
    body: formData,
  });
}

export function importBudgetQuantityMap(
  budgetId: number,
  file: File,
  options?: { signal?: AbortSignal },
): Promise<BudgetQuantityImportPreview> {
  const formData = new FormData();
  formData.append("document", file, file.name || "mapa-quantidades");

  const controller = new AbortController();
  const onExternalAbort = () => controller.abort();
  if (options?.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener("abort", onExternalAbort, { once: true });
    }
  }

  const timeoutHandle: ReturnType<typeof setTimeout> = globalThis.setTimeout(
    () => controller.abort(),
    360_000,
  );

  return request<BudgetQuantityImportPreview>(
    `/api/budgets/${budgetId}/imports/quantity-map`,
    {
      method: "POST",
      body: formData,
      signal: controller.signal,
    },
  ).catch((error: unknown) => {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new ApiError(
        408,
        JSON.stringify({
          title: "Tempo de espera excedido.",
          detail:
            "A importação demorou mais de 6 minutos. Tente novamente dentro de instantes ou contacte o suporte para validar a configuração de IA.",
        }),
      );
    }

    throw error;
  }).finally(() => {
    options?.signal?.removeEventListener("abort", onExternalAbort);
    globalThis.clearTimeout(timeoutHandle);
  });
}

export function startBudgetQuantityImportJob(
  budgetId: number,
  file: File,
): Promise<BudgetQuantityImportJobCreated> {
  const formData = new FormData();
  formData.append("document", file, file.name || "mapa-quantidades");

  return request<BudgetQuantityImportJobCreated>(
    `/api/budgets/${budgetId}/quantity-map/import`,
    {
      method: "POST",
      body: formData,
    },
  );
}

export function getImportJobStatus(
  jobId: string,
): Promise<BudgetQuantityImportJobStatusResponse> {
  return request<BudgetQuantityImportJobStatusResponse>(
    `/api/import-jobs/${jobId}`,
  );
}

export function deleteBudgetDocument(
  budgetId: number,
  documentId: number,
): Promise<void> {
  return request<void>(`/api/budgets/${budgetId}/documents/${documentId}`, {
    method: "DELETE",
  });
}

export function getBudgetDocument(
  budgetId: number,
  documentId: number,
): Promise<BudgetDocument> {
  return request<BudgetDocument>(
    `/api/budgets/${budgetId}/documents/${documentId}`,
  );
}

export function getBudgetLineCostSuggestions(
  budgetId: number,
  payload: BudgetLineCostSuggestionRequest,
): Promise<BudgetLineCostSuggestion[]> {
  const params = new URLSearchParams();
  params.set("query", payload.query);
  if (payload.unit) {
    params.set("unit", payload.unit);
  }
  if (payload.limit) {
    params.set("limit", payload.limit.toString());
  }

  return request<BudgetLineCostSuggestion[]>(
    `/api/budgets/${budgetId}/lines/suggestions?${params.toString()}`,
  );
}

export type ReindexBudgetLinesResponse = {
  queuedBudgets: number;
};

export function reindexBudgetLines(): Promise<ReindexBudgetLinesResponse> {
  return request<ReindexBudgetLinesResponse>("/api/ai/budgets/reindex", {
    method: "POST",
  });
}

export type BudgetLineSuggestionSettings = {
  limit: number | null;
};

export function getBudgetLineSuggestionSettings(): Promise<BudgetLineSuggestionSettings> {
  return request<BudgetLineSuggestionSettings>(
    "/api/ai/budgets/suggestions/settings",
  );
}

export function updateBudgetLineSuggestionSettings(
  payload: BudgetLineSuggestionSettings,
): Promise<BudgetLineSuggestionSettings> {
  return request<BudgetLineSuggestionSettings>(
    "/api/ai/budgets/suggestions/settings",
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export function reprocessBudgetDocument(
  budgetId: number,
  documentId: number,
): Promise<BudgetDocument> {
  return request<BudgetDocument>(
    `/api/budgets/${budgetId}/documents/${documentId}/reprocess`,
    {
      method: "POST",
    },
  );
}

export type ExtractBudgetDocumentsResult = {
  cardId: string;
  documentCount: number;
};

export function extractBudgetDocuments(
  budgetId: number,
): Promise<ExtractBudgetDocumentsResult> {
  return request<ExtractBudgetDocumentsResult>(
    `/api/budgets/${budgetId}/extract`,
    {
      method: "POST",
    },
  );
}


export const getBudgetDocumentDownloadUrl = (
  budgetId: number,
  documentId: number,
): string => {
  return `${apiBaseUrl}/api/budgets/${budgetId}/documents/${documentId}/download`;
};

async function downloadBudgetExport(
  budgetId: number,
  path: string,
  fallbackFileName: string,
): Promise<void> {
  const response = await fetch(`${apiBaseUrl}/api/budgets/${budgetId}/${path}`, {
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition");
  let fileName = fallbackFileName;

  if (disposition) {
    const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';\n]+)/i);
    if (match?.[1]) {
      fileName = decodeURIComponent(match[1]);
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportBudgetToExcel(budgetId: number): Promise<void> {
  return downloadBudgetExport(budgetId, "export/excel", `Orcamento_${budgetId}.xlsx`);
}

export function exportBudgetToPdf(budgetId: number): Promise<void> {
  return downloadBudgetExport(budgetId, "export/pdf", `Orcamento_${budgetId}.pdf`);
}

export function getBudgetAreaTypes(): Promise<BudgetAreaType[]> {
  return request<BudgetAreaType[]>("/api/budget-area-types");
}

export function getBudgetChapterTypes(): Promise<BudgetChapterType[]> {
  return request<BudgetChapterType[]>("/api/budget-chapter-types");
}

export function createBudgetAreaType(
  payload: BudgetAreaTypePayload,
): Promise<BudgetAreaType> {
  return request<BudgetAreaType>("/api/budget-area-types", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateBudgetAreaType(
  areaTypeId: number,
  payload: BudgetAreaTypePayload,
): Promise<BudgetAreaType> {
  return request<BudgetAreaType>(
    `/api/budget-area-types/${areaTypeId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteBudgetAreaType(
  areaTypeId: number,
): Promise<void> {
  return request<void>(`/api/budget-area-types/${areaTypeId}`, {
    method: "DELETE",
  });
}

export function createBudgetChapterType(
  payload: BudgetChapterTypePayload,
): Promise<BudgetChapterType> {
  return request<BudgetChapterType>("/api/budget-chapter-types", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateBudgetChapterType(
  chapterTypeId: number,
  payload: BudgetChapterTypePayload,
): Promise<BudgetChapterType> {
  return request<BudgetChapterType>(
    `/api/budget-chapter-types/${chapterTypeId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    },
  );
}

export function deleteBudgetChapterType(
  chapterTypeId: number,
): Promise<void> {
  return request<void>(`/api/budget-chapter-types/${chapterTypeId}`, {
    method: "DELETE",
  });
}

// --- Margin Alert Ranges ---

export interface MarginAlertRange {
  id: number;
  minPercent: number;
  maxPercent: number | null;
  color: string;
  label: string | null;
  position: number;
}

export interface MarginAlertRangePayload {
  minPercent: number;
  maxPercent: number | null;
  color: string;
  label: string | null;
  position: number;
}

export function getMarginAlertRanges(): Promise<MarginAlertRange[]> {
  return request<MarginAlertRange[]>('/api/settings/margin-alert-ranges');
}

export function saveMarginAlertRanges(
  ranges: MarginAlertRangePayload[],
): Promise<MarginAlertRange[]> {
  return request<MarginAlertRange[]>('/api/settings/margin-alert-ranges', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ranges }),
  });
}

export function resolveMarginColor(
  marginPercent: number,
  ranges: MarginAlertRange[],
): string | null {
  if (ranges.length === 0) return null;
  const sorted = [...ranges].sort((a, b) => a.position - b.position);
  for (const range of sorted) {
    const aboveMin = marginPercent >= range.minPercent;
    const belowMax = range.maxPercent == null || marginPercent < range.maxPercent;
    if (aboveMin && belowMax) return range.color;
  }
  return null;
}

// AI Settings

export interface AiSettings {
  hasAnthropicApiKey: boolean;
  anthropicModel: string | null;
  anthropicMaxTokens: number;
  anthropicMaxPagesPerDocument: number;
  defaultSuggestionMarginPercent: number | null;
}

export interface SaveAiSettingsPayload {
  anthropicApiKey?: string | null;
  clearAnthropicApiKey?: boolean;
  anthropicModel?: string | null;
  anthropicMaxTokens?: number | null;
  anthropicMaxPagesPerDocument?: number | null;
  defaultSuggestionMarginPercent?: number | null;
}

export function getAiSettings(): Promise<AiSettings> {
  return request<AiSettings>('/api/settings/ai');
}

export function saveAiSettings(payload: SaveAiSettingsPayload): Promise<AiSettings> {
  return request<AiSettings>('/api/settings/ai', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// Financial Settings (parâmetros financeiros)

export interface FinancialSettings {
  /** IVA padrão como valor percentual (ex.: 23 = 23%). */
  defaultVatRate: number;
  /** Markup base (% sobre custo). null se não configurado. */
  defaultMarkupPercent: number | null;
  /** Limiar de derrapagem (% do custo real sobre o previsto) no Mapa de Custos. */
  costOverrunThresholdPercent: number;
  /** Período de garantia por defeito (meses) aplicado ao encerrar uma obra [4.7.1]. */
  defaultWarrantyMonths: number;
}

export interface SaveFinancialSettingsPayload {
  defaultVatRate?: number | null;
  defaultMarkupPercent?: number | null;
  clearDefaultMarkupPercent?: boolean;
  /**
   * Limiar de derrapagem (% do custo real sobre o previsto). Obrigatório: o backend
   * atribui-o sempre, pelo que omiti-lo zera o valor guardado.
   */
  costOverrunThresholdPercent: number;
  /**
   * Período de garantia por defeito (meses). Obrigatório pelo mesmo motivo do
   * limiar de derrapagem: o backend atribui-o sempre no save load-all/save-all.
   */
  defaultWarrantyMonths: number;
}

export function getFinancialSettings(): Promise<FinancialSettings> {
  return request<FinancialSettings>('/api/settings/financial');
}

export function saveFinancialSettings(
  payload: SaveFinancialSettingsPayload,
): Promise<FinancialSettings> {
  return request<FinancialSettings>('/api/settings/financial', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// Indirect Cost Settings (critério de rateio de custos indiretos) [4.4.5]

export interface IndirectCostSettings {
  /** Critério de rateio: faturacao | horas | m2 | igual. */
  criterion: string;
}

export function getIndirectCostSettings(): Promise<IndirectCostSettings> {
  return request<IndirectCostSettings>('/api/settings/indirect-cost');
}

export function saveIndirectCostSettings(
  payload: IndirectCostSettings,
): Promise<IndirectCostSettings> {
  return request<IndirectCostSettings>('/api/settings/indirect-cost', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

// --- Budget Percentage Rules ---

export interface BudgetPercentageRule {
  id: number;
  description: string;
  defaultPercentage: number;
  position: number;
}

export interface BudgetPercentageRulePayload {
  description: string;
  defaultPercentage: number;
  position: number;
}

export function getBudgetPercentageRules(): Promise<BudgetPercentageRule[]> {
  return request<BudgetPercentageRule[]>('/api/settings/budget-percentage-rules');
}

export function saveBudgetPercentageRules(
  rules: BudgetPercentageRulePayload[],
): Promise<BudgetPercentageRule[]> {
  return request<BudgetPercentageRule[]>('/api/settings/budget-percentage-rules', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rules }),
  });
}

const normalizePercentageRuleKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export function findMatchingPercentageRule(
  description: string,
  rules: BudgetPercentageRule[],
): BudgetPercentageRule | null {
  if (rules.length === 0) return null;
  const key = normalizePercentageRuleKey(description);
  if (key === '') return null;
  for (const rule of rules) {
    const ruleKey = normalizePercentageRuleKey(rule.description);
    if (ruleKey !== '' && key.includes(ruleKey)) {
      return rule;
    }
  }
  return null;
}

// --- Article Classification Rules ---

export interface ArticleClassificationRule {
  id: number;
  matchType: string;
  matchValue: string;
  classificationCode: string;
  position: number;
}

export interface ArticleClassificationRulePayload {
  matchType: string;
  matchValue: string;
  classificationCode: string;
  position: number;
}

export function getArticleClassificationRules(): Promise<ArticleClassificationRule[]> {
  return request<ArticleClassificationRule[]>('/api/settings/article-classification-rules');
}

export function saveArticleClassificationRules(
  rules: ArticleClassificationRulePayload[],
): Promise<ArticleClassificationRule[]> {
  return request<ArticleClassificationRule[]>('/api/settings/article-classification-rules', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rules }),
  });
}

// --- Expense Category Rules (Lote A-18) ---

export interface ExpenseCategoryRule {
  id: number;
  matchType: string;
  matchValue: string;
  categoryCode: string;
  position: number;
}

export interface ExpenseCategoryRulePayload {
  matchType: string;
  matchValue: string;
  categoryCode: string;
  position: number;
}

export function getExpenseCategoryRules(): Promise<ExpenseCategoryRule[]> {
  return request<ExpenseCategoryRule[]>('/api/settings/expense-category-rules');
}

export function saveExpenseCategoryRules(
  rules: ExpenseCategoryRulePayload[],
): Promise<ExpenseCategoryRule[]> {
  return request<ExpenseCategoryRule[]>('/api/settings/expense-category-rules', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ rules }),
  });
}

// --- Anomaly Detection Settings (Lote A-22 [2.1.9]) ---

export interface AnomalySettings {
  nifCheckEnabled: boolean;
  newSupplierEnabled: boolean;
  valueAboveUsualEnabled: boolean;
  valueAboveUsualMultiplier: number;
  valueAboveUsualMinHistory: number;
  weekendDateEnabled: boolean;
  atypicalCategoryEnabled: boolean;
}

export function getAnomalySettings(): Promise<AnomalySettings> {
  return request<AnomalySettings>('/api/settings/anomaly-detection');
}

export function saveAnomalySettings(
  settings: AnomalySettings,
): Promise<AnomalySettings> {
  return request<AnomalySettings>('/api/settings/anomaly-detection', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
}

// --- Credit Control Settings (Lote A-25 [3.6.x]) — risco, bloqueio, JAL & cobrança ---

export interface CreditControlSettings {
  /** Dias em atraso (> este) que tornam o risco pelo menos amarelo. */
  riskYellowOverdueDays: number;
  /** Dívida vencida (>= este) que torna o risco pelo menos amarelo. */
  riskYellowOverdueAmount: number;
  /** Dias em atraso (> este) que tornam o risco vermelho. */
  riskRedOverdueDays: number;
  /** Dívida vencida (>= este) que torna o risco vermelho. */
  riskRedOverdueAmount: number;
  /** Processo jurídico ativo ⇒ risco vermelho. */
  riskRedOnLegalProcess: boolean;
  /** Bloquear emissão quando a dívida vencida atinge o limite. */
  blockOnOverdueAmount: boolean;
  /** Limite de dívida vencida que aciona o bloqueio. */
  blockOverdueAmountThreshold: number;
  /** Bloquear quando saldo + nova fatura excede o limite de crédito. */
  blockOnCreditLimitExceeded: boolean;
  /** Bloquear quando o cliente está em risco vermelho. */
  blockOnRedRisk: boolean;
  /** Dias mínimos em atraso para uma fatura ser migrável (JAL). */
  jalOverdueDays: number;
  /** Dias antes do vencimento em que o lembrete "pré-vencimento" é sugerido. */
  collectionReminderLeadDays: number;
}

export function getCreditControlSettings(): Promise<CreditControlSettings> {
  return request<CreditControlSettings>('/api/settings/credit-control');
}

export function saveCreditControlSettings(
  settings: CreditControlSettings,
): Promise<CreditControlSettings> {
  return request<CreditControlSettings>('/api/settings/credit-control', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
}

// --- Treasury Settings (Lote A-26 [3.3.x]) — saldo de partida + limiares do semáforo ---

export interface TreasurySettings {
  /** Saldo de partida (caixa+bancos), introduzido manualmente. */
  openingBalance: number;
  /** Data a que o saldo de partida se refere (null ⇒ início do forecast / hoje). */
  openingBalanceAsOf: string | null;
  /** Prazo (dias) para derivar a data de pagamento das despesas a partir da data do documento. */
  defaultSupplierPaymentDays: number;
  /** Dia do mês seguinte em que as despesas se pagam por regra (manda sobre os dias); null ⇒ conta-se em dias. */
  supplierPaymentDayOfMonth: number | null;
  /** Saldo projetado abaixo deste valor ⇒ pelo menos amarelo. */
  projectedBalanceWarningThreshold: number;
  /** Saldo projetado abaixo deste valor ⇒ vermelho. */
  projectedBalanceCriticalThreshold: number;
  /** Janela (dias) "a vencer/pagamento próximo" usada nos alertas. */
  upcomingDueDays: number;
  /** Confirming: plafond, taxa anual e dias entre o banco pagar ao fornecedor e debitar a liquidação. */
  confirmingPlafond?: number;
  confirmingAnnualRatePercent?: number;
  confirmingTermDays?: number;
  /** Nºs dos contratos de Confirming tal como aparecem no extrato, separados por vírgula. */
  confirmingContractReferences?: string | null;
}

export function getTreasurySettings(): Promise<TreasurySettings> {
  return request<TreasurySettings>('/api/settings/treasury');
}

export function saveTreasurySettings(
  settings: TreasurySettings,
): Promise<TreasurySettings> {
  return request<TreasurySettings>('/api/settings/treasury', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(settings),
  });
}
